import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  validateToken,
  registerCleaner,
  claimTask,
  getStoredCleanerId,
  setStoredCleanerId,
  type PortalTask,
  type PortalValidateResponse,
} from "@/lib/cleanerPortalApi";
import { isToday } from "date-fns";
import { formatScheduledDate, parseLocalDateOnly } from "@/lib/dateUtils";
import { Loader2, Play, User, CalendarDays } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

export default function CleanerPortalToken() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [step, setStep] = useState<"loading" | "name" | "portal" | "error">("loading");
  const [displayName, setDisplayName] = useState("");
  const [data, setData] = useState<PortalValidateResponse | null>(null);
  const [cleanerId, setCleanerId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [claimingTaskId, setClaimingTaskId] = useState<string | null>(null);

  const rawToken = token ?? "";

  useEffect(() => {
    if (!rawToken) {
      setStep("error");
      setErrorMessage(t("missingAccessLink"));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await validateToken(rawToken);
        if (cancelled) return;
        setData(res);
        const stored = getStoredCleanerId(rawToken);
        if (stored) {
          setCleanerId(stored);
          setStep("portal");
        } else {
          setStep("name");
        }
      } catch (e) {
        if (cancelled) return;
        setStep("error");
        setErrorMessage(e instanceof Error ? e.message : t("invalidOrExpiredLink"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rawToken, t]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rawToken || !data) return;
    try {
      const res = await registerCleaner(rawToken, displayName.trim());
      setCleanerId(res.cleaner_id);
      setStoredCleanerId(rawToken, res.cleaner_id);
      setStep("portal");
      toast({ title: t("welcome"), description: t("welcomeClaimTasks") });
    } catch (e) {
      toast({
        title: t("error"),
        description: e instanceof Error ? e.message : t("registrationFailed"),
        variant: "destructive",
      });
    }
  };

  const handleClaim = async (taskId: string) => {
    if (!rawToken || !cleanerId) return;
    setClaimingTaskId(taskId);
    try {
      await claimTask(rawToken, cleanerId, taskId);
      toast({ title: t("taskClaimed"), description: t("taskClaimedDesc") });
      const res = await validateToken(rawToken);
      setData(res);
    } catch (e) {
      toast({
        title: t("error"),
        description: e instanceof Error ? e.message : t("couldNotClaimTask"),
        variant: "destructive",
      });
    } finally {
      setClaimingTaskId(null);
    }
  };

  if (step === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invalidLink")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground mt-3">{t("askOwnerNewLink")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === "name") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {t("whatShouldWeCallYou")}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{t("portalEnterNameHint")}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="displayName">{t("yourName")}</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t("namePlaceholderExample")}
                  className="mt-1"
                />
              </div>
              <Button type="submit" className="w-full">
                {t("continue")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tasks = data?.tasks ?? [];
  const canStart = (task: PortalTask) =>
    task.assigned_type === "unassigned" && !task.cleaner_id && ["scheduled", "in_progress"].includes(task.status);

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-xl font-bold">{data?.property_name ?? t("cleaningPortal")}</h1>
          {data?.property_address && (
            <p className="text-sm text-muted-foreground">{data.property_address}</p>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="w-4 h-4" />
              {t("upcomingTasks")}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{t("upcomingTasksTapStart")}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("noUpcomingTasks14Days")}</p>
            ) : (
              <ul className="space-y-2">
                {tasks.map((task) => (
                  <li
                    key={task.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg border bg-card"
                  >
                    <div>
                      <p className="font-medium">
                        {formatScheduledDate(task.scheduled_date, "EEE, MMM d, yyyy")}
                        {isToday(parseLocalDateOnly(task.scheduled_date)) && (
                          <span className="ml-2 text-xs text-muted-foreground">{t("todayParen")}</span>
                        )}
                      </p>
                      {task.override_check_out_time && (
                        <p className="text-xs text-muted-foreground">
                          {t("checkoutBy")} {task.override_check_out_time}
                        </p>
                      )}
                      {task.notes && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.notes}</p>
                      )}
                      {task.assigned_type === "owner" && (
                        <p className="text-xs text-muted-foreground">{t("selfCleanOwner")}</p>
                      )}
                      {task.assigned_type === "cleaner" && task.cleaner_id && task.cleaner_id !== cleanerId && (
                        <p className="text-xs text-muted-foreground">{t("assignedToAnotherCleaner")}</p>
                      )}
                      {task.assigned_type === "cleaner" && task.cleaner_id === cleanerId && (
                        <p className="text-xs text-primary font-medium">{t("assignedToYou")}</p>
                      )}
                    </div>
                    {canStart(task) && (
                      <Button
                        size="sm"
                        onClick={() => handleClaim(task.id)}
                        disabled={claimingTaskId === task.id}
                        className="shrink-0"
                      >
                        {claimingTaskId === task.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-1" />
                        )}
                        {t("startClean")}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
